import { ChangeDetectionStrategy, Component, effect, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { ContextMenuDividerComponent } from '@frontendlib/components/features/popover/components/context-menu-divider/context-menu-divider.component';
import { ContextMenuHeadingComponent } from '@frontendlib/components/features/popover/components/context-menu-heading/context-menu-heading.component';
import { ContextMenuItemComponent } from '@frontendlib/components/features/popover/components/context-menu-item/context-menu-item.component';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { dateRangeChanges, setDateRangeSilently } from '@modules/common/utils/date-range-filter.util';
import { ElementsModule } from '@modules/elements/elements.module';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { DatePicker2Component } from '@modules/form-controls/components/date-picker-2/date-picker-2.component';
import { type BoardsStoreInstance } from '../../../services/boards.store';

/** Sorting and filters of the folder list — the same menu shape as Mail/Files' Folders, Stitch filter
 * included (matched client-side against each folder's `stitchedWith`, like Pinned/Flagged are). */
@Component({
  selector: 'app-boards-folders-list-context-menu',
  templateUrl: './folders-list-context-menu.component.html',
  styleUrls: ['../../../../notes/components/notes-list-context-menu/notes-list-context-menu.component.less'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconComponent,
    ContextMenuItemComponent,
    ContextMenuDividerComponent,
    ContextMenuHeadingComponent,
    DatePicker2Component,
    DateFormatPipe,
    Button2Component,
    ElementsModule,
  ],
})
export class FoldersListContextMenuComponent {
  /** Set by the opening directive on the created instance before first render (`ContextMenuDirective` assigns
   * component fields directly, so this cannot be a signal input). */
  public store: BoardsStoreInstance;

  protected readonly fromDate = new FormControl<Date | null>(null);
  protected readonly toDate = new FormControl<Date | null>(null);
  protected readonly dateButtonSelected = signal<'from' | 'to'>('from');

  constructor() {
    effect(() => {
      const { from, to } = this.store.folderOptions();
      setDateRangeSilently(this.fromDate, this.toDate, { from, to });
    });

    dateRangeChanges(this.fromDate, this.toDate)
      .pipe(takeUntilDestroyed())
      .subscribe(({ from, to }) => {
        const options = this.store.folderOptions();
        if (from?.getTime() !== options.from?.getTime() || to?.getTime() !== options.to?.getTime()) {
          this.store.setFolderDateRange(from, to);
        }
      });
  }

  protected clearSelectedDate(): void {
    this.store.clearFolderDateRange();
  }
}
